import { stringify } from '@dittolive/anvil'
import { useQuery } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useCallback, useEffect, useState } from 'react'

import { useDevicesTableStore } from '../tabs/devices/tableStore'
import useDeviceRPC, {
  Device,
  DeviceRequestType,
  LastUpdatedOption,
} from './useDeviceRPC'

dayjs.extend(utc)

/**
 * Downloads device information as a JSON file.
 * Uses the raw document from the __small_peer_info collection to ensure
 * all fields are included, not just those mapped to the typed Device properties.
 * @param device The device to download
 */
export function downloadDeviceInfoAsJson(device: Device) {
  const date = dayjs()
  // Use rawDocument to get the complete document from __small_peer_info,
  // falling back to the typed device object if rawDocument is not available
  const dataToDownload = device.rawDocument ?? device
  const jsonData = stringify(dataToDownload, null, 2)
  const blob = new Blob([jsonData], {
    type: 'application/json;charset=utf-8',
  })
  const blobUrl = URL.createObjectURL(blob)
  const fileNameFriendlyDate = date.toISOString().replaceAll(':', '-')
  const fileName = `small-peer-data-${device.peerKey}-${fileNameFriendlyDate}.json`
  const link = document.createElement('a')
  link.setAttribute('href', blobUrl)
  link.setAttribute('style', 'display: none')
  link.setAttribute('download', fileName)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(blobUrl)
}

export type Params = {
  /**
   * The ID of the app to fetch devices for
   */
  appId: string
  /**
   * The cloud domain URL for the app
   */
  cloudUrl?: string
  /**
   * Optional pagination settings. If provided, the hook will use paginated data fetching
   * instead of fetching all devices at once.
   */
  pagination?: {
    /**
     * The current page number (0-based)
     */
    page: number
    /**
     * The number of devices per page
     */
    pageSize: number
  }
}

type Return = {
  /**
   * An error message if an error occurred while fetching the devices
   */
  errorMessage?: string
  /**
   * A callback to fetch the logs for a device, provided a specific attachment ID
   */
  fetchLogs: (attachmentId: string) => Promise<Uint8Array[] | null>
  /**
   * A callback to fetch the bundle for a device, provided a specific attachment ID
   */
  fetchBundle: (attachmentId: string) => Promise<Uint8Array[] | null>
  /**
   * A callback to fetch the flame graph for a device, provided a specific attachment ID
   */
  fetchFlamegraph: (attachmentId: string) => Promise<Uint8Array[] | null>
  /**
   * A callback to fetch the metrics for a device, provided a specific attachment ID
   */
  fetchMetrics: (attachmentId: string) => Promise<Uint8Array[] | null>
  /**
   * A callback to request logs for a device, provided its peer key.
   * This will refresh the devices list after the logs request is made.
   */
  requestLogs: (peerKey: string) => Promise<void>
  /**
   * A callback to request bundle for a device, provided its peer key.
   * This will refresh the devices list after the bundle request is made.
   */
  requestBundle: (peerKey: string) => Promise<void>
  /**
   * A callback to request flamegraph for a device, provided its peer key.
   * This will refresh the devices list after the flamegraph request is made.
   */
  requestFlamegraph: (peerKey: string) => Promise<void>
  /**
   * A callback to request metrics for a device, provided its peer key.
   * This will refresh the devices list after the metrics request is made.
   */
  requestMetrics: (peerKey: string) => Promise<void>
  /**
   * A callback to request logs for a list of devices, provided their peer keys.
   * This will refresh the devices list after the logs request is made.
   */
  bulkRequestLogs: (peerKeys: string[]) => Promise<void>
  /**
   * A callback to request the data bundle for a list of devices, provided their peer keys.
   * This will refresh the devices list after the bundle request is made.
   */
  bulkRequestBundle: (peerKeys: string[]) => Promise<void>
  /**
   * A callback to request flamegraph for a list of devices, provided their peer keys.
   * This will refresh the devices list after the flamegraph request is made.
   */
  bulkRequestFlamegraph: (peerKeys: string[]) => Promise<void>
  /**
   * A callback to request metrics for a list of devices, provided their peer keys.
   * This will refresh the devices list after the metrics request is made.
   */
  bulkRequestMetrics: (peerKeys: string[]) => Promise<void>
  /**
   * A callback to cancel a pending data request for a device.
   * This will refresh the devices list after cancellation.
   */
  cancelDataRequest: (
    peerKey: string,
    requestType: DeviceRequestType,
  ) => Promise<void>
  /**
   * A callback to cancel pending data requests for multiple devices.
   * This will refresh the devices list after cancellation.
   */
  bulkCancelDataRequests: (
    peerKeys: string[],
    requestType: 'logs' | 'bundle' | 'flamegraph',
  ) => Promise<void>
  /**
   * The total count of devices
   */
  deviceCount?: number
  /**
   * Separate paginated devices result (raw DQL results, not converted to Device objects)
   */
  paginatedDevices?: Device[]
  /**
   * A method to find a device by its name or ID
   */
  findDevice: (
    nameOrId: string,
    lastUpdatedFilter: LastUpdatedOption,
  ) => Promise<Device[]>
  /**
   * A method to find devices by name or ID with pagination
   */
  findDevicePaginated: (
    nameOrId: string,
    page: number,
    pageSize: number,
    lastUpdatedFilter: LastUpdatedOption,
  ) => Promise<Device[]>
  /**
   * Gets the total count of devices matching a search term
   */
  fetchSearchCount: (
    nameOrId: string,
    lastUpdatedFilter: LastUpdatedOption,
  ) => Promise<number>
  /**
   * Fetches devices by their peer keys
   *
   * @param peerKeys - Array of peer keys to fetch devices for
   * @returns An array of devices with the given peer keys
   */
  fetchDevicesByPeerKeys: (peerKeys: string[]) => Promise<Device[]>
  /**
   * Fetches a merged sample of device metadata from the most recently
   * updated devices, independent of the table's filters and pagination.
   * Used to populate metadata column picker options so keys that only
   * exist on devices outside the current table view can still be selected.
   */
  fetchMetadataSample: () => Promise<Record<string, unknown>>
  /**
   * Gets the total count of devices matching a search term
   */
  isLoadingPaginated: boolean
  /**
   * Whether the paginated devices are currently being refetched
   */
  isRefetchingPaginated: boolean
  /**
   * A callback to refetch the paginated devices
   */
  refetchPaginated: () => Promise<void>
  /**
   * Pagination controls and state. Only present when pagination is enabled.
   */
  pagination?: {
    /**
     * The current page number (0-based)
     */
    currentPage: number
    /**
     * The number of devices per page
     */
    pageSize: number
    /**
     * The total number of pages
     */
    totalPages: number
    /**
     * Whether there is a next page available
     */
    hasNextPage: boolean
    /**
     * Whether there is a previous page available
     */
    hasPreviousPage: boolean
    /**
     * Navigate to a specific page
     */
    goToPage: (page: number) => void
  }
  search: {
    /**
     * The current search query
     */
    query: string | undefined
    /**
     * The search count
     */
    count: number
    /**
     * The current search results
     */
    results: Device[]
    /**
     * Whether the search is currently being performed
     */
    isLoading: boolean
    /**
     * Whether in search mode
     */
    enabled: boolean
    /**
     * The current search page
     */
    page: number
    /**
     * The current search page size
     */
    pageSize: number
    /**
     * Set the current search page
     */
    setSearchPage: (page: number) => void
    /**
     * Set the search query
     */
    setSearchQuery: (query: string | undefined) => void
  }
  /**
   * The current last updated filter value
   */
  lastUpdatedFilter: LastUpdatedOption | undefined
  /**
   * Set the last updated filter
   */
  setLastUpdatedFilter: (filter: LastUpdatedOption) => void
}

export const BASE_QUERY_KEY = 'getDevices'

/** A hook that provides device dashboard data and log requests operations. */
export default function useDevices({
  appId,
  cloudUrl,
  pagination,
}: Params): Return {
  const {
    accessToken,
    getDevicePageAndCount,
    findDevice,
    findDevicePaginated,
    fetchSearchCount,
    fetchDevicesByPeerKeys,
    fetchMetadataSample,
    requestLogs,
    requestBundle,
    requestFlamegraph,
    requestMetrics,
    fetchLogs,
    fetchBundle,
    fetchFlamegraph,
    fetchMetrics,
    bulkRequestLogs,
    bulkRequestBundle,
    bulkRequestFlamegraph,
    bulkRequestMetrics,
    cancelDataRequest,
    bulkCancelDataRequests,
    refreshToken,
  } = useDeviceRPC({ appId, cloudUrl })

  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const [didRetry, setDidRetry] = useState(false)

  const [currentPage, setCurrentPage] = useState(pagination?.page || 0)
  const { lastUpdatedFilter, setLastUpdatedFilter } = useDevicesTableStore()

  /**
   * Paginated devices query
   */
  const {
    data,
    isPending: isLoadingPaginated,
    isRefetching: isRefetchingPaginated,
    error,
    refetch: refetchPaginated,
  } = useQuery({
    queryKey: [
      BASE_QUERY_KEY,
      'paginated',
      appId,
      currentPage,
      pagination?.pageSize,
      lastUpdatedFilter,
    ],
    queryFn: async () => {
      if (!pagination) return { devices: [], count: 0 }
      return await getDevicePageAndCount(
        currentPage,
        pagination.pageSize,
        lastUpdatedFilter,
      )
    },
    enabled: !!accessToken && !!pagination,
  })

  // Devices and count from the query result
  const paginatedDevices = data?.devices || []
  const deviceCount = data?.count || 0

  // Search
  const [searchQuery, setSearchQuery] = useState<string | undefined>()
  const [searchResults, setSearchResults] = useState<Device[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [isInSearchMode, setIsInSearchMode] = useState(false)
  const [searchPage, setSearchPage] = useState(0)
  const [searchTotalCount, setSearchTotalCount] = useState(0)

  const searchPageSize = pagination?.pageSize || 25

  const handleTryAgain = useCallback(async () => {
    setDidRetry(true)
    await refreshToken()
    await refetchPaginated()
  }, [refreshToken, refetchPaginated])

  /**
   * Effect for search term changes
   * This is for when the user is searching for devices
   */
  useEffect(() => {
    if (searchQuery?.trim()) {
      setIsInSearchMode(true)
      setIsSearching(true)
      const performSearch = async () => {
        try {
          const [results, totalCount] = await Promise.all([
            findDevicePaginated(
              searchQuery.trim(),
              0,
              searchPageSize,
              lastUpdatedFilter,
            ),
            fetchSearchCount(searchQuery.trim(), lastUpdatedFilter),
          ])
          setSearchResults(results)
          setSearchTotalCount(totalCount)
        } catch (error) {
          setSearchResults([])
          setSearchTotalCount(0)
        } finally {
          setIsSearching(false)
        }
      }
      performSearch()
    } else {
      setIsInSearchMode(false)
      setSearchResults([])
      setSearchTotalCount(0)
      setSearchPage(0)
      setIsSearching(false)
    }
  }, [
    searchQuery,
    findDevicePaginated,
    fetchSearchCount,
    lastUpdatedFilter,
    searchPageSize,
  ])

  /**
   * A separate effect for immediate pagination when in search mode
   * This is for when the user is paginating through search results
   */
  useEffect(() => {
    if (searchQuery?.trim() && isInSearchMode && searchPage > 0) {
      const fetchPage = async () => {
        setIsSearching(true)
        try {
          const results = await findDevicePaginated(
            searchQuery.trim(),
            searchPage,
            searchPageSize,
            lastUpdatedFilter,
          )
          setSearchResults(results)
        } catch (error) {
          setSearchResults([])
        } finally {
          setIsSearching(false)
        }
      }
      fetchPage()
    }
  }, [
    searchPage,
    searchQuery,
    isInSearchMode,
    findDevicePaginated,
    lastUpdatedFilter,
    searchPageSize,
  ])

  /**
   * Reset search page when search term changes
   */
  useEffect(() => {
    setSearchPage(0)
  }, [searchQuery])

  /**
   * An effect to properly handle errors when fetching the device. This will
   * primarily serve to set the error message, with the exception of 401 errors.
   */
  useEffect(() => {
    if (!error) return

    const fallback = 'An error occurred while fetching devices'
    if (isAxiosError(error)) {
      if (error.response?.status === 401 && !didRetry) {
        handleTryAgain()
      } else {
        setErrorMessage(error.response?.data?.message || fallback)
      }
    } else if (error instanceof Error) {
      setErrorMessage(error.message || fallback)
    } else {
      setErrorMessage(fallback)
    }
  }, [error, handleTryAgain, didRetry])

  const handleRequestLogs = useCallback(
    async (peerKey: string) => {
      await requestLogs(peerKey)
      await refetchPaginated()
    },
    [requestLogs, refetchPaginated],
  )

  const handleRequestBundle = useCallback(
    async (peerKey: string) => {
      await requestBundle(peerKey)
      await refetchPaginated()
    },
    [requestBundle, refetchPaginated],
  )

  const handleRequestFlamegraph = useCallback(
    async (peerKey: string) => {
      await requestFlamegraph(peerKey)
      await refetchPaginated()
    },
    [requestFlamegraph, refetchPaginated],
  )

  const handleRequestMetrics = useCallback(
    async (peerKey: string) => {
      await requestMetrics(peerKey)
      await refetchPaginated()
    },
    [requestMetrics, refetchPaginated],
  )

  const handleBulkRequestLogs = useCallback(
    async (peerKeys: string[]) => {
      await bulkRequestLogs(peerKeys)
      await refetchPaginated()
    },
    [bulkRequestLogs, refetchPaginated],
  )

  const handleBulkRequestBundle = useCallback(
    async (peerKeys: string[]) => {
      await bulkRequestBundle(peerKeys)
      await refetchPaginated()
    },
    [bulkRequestBundle, refetchPaginated],
  )

  const handleBulkRequestFlamegraph = useCallback(
    async (peerKeys: string[]) => {
      await bulkRequestFlamegraph(peerKeys)
      await refetchPaginated()
    },
    [bulkRequestFlamegraph, refetchPaginated],
  )

  const handleBulkRequestMetrics = useCallback(
    async (peerKeys: string[]) => {
      await bulkRequestMetrics(peerKeys)
      await refetchPaginated()
    },
    [bulkRequestMetrics, refetchPaginated],
  )

  const handleRefetchPaginated = useCallback(async () => {
    await refetchPaginated()
  }, [refetchPaginated])

  const handleCancelDataRequest = useCallback(
    async (peerKey: string, requestType: DeviceRequestType) => {
      await cancelDataRequest(peerKey, requestType)
      await refetchPaginated()
    },
    [cancelDataRequest, refetchPaginated],
  )

  const handleBulkCancelDataRequests = useCallback(
    async (
      peerKeys: string[],
      requestType: 'logs' | 'bundle' | 'flamegraph',
    ) => {
      await bulkCancelDataRequests(peerKeys, requestType)
      await refetchPaginated()
    },
    [bulkCancelDataRequests, refetchPaginated],
  )

  // Pagination navigation functions
  const goToPage = useCallback(
    (page: number) => {
      if (!pagination || !deviceCount) return
      const totalPages = Math.ceil(deviceCount / pagination.pageSize)
      const validPage = Math.max(0, Math.min(page, totalPages - 1))
      setCurrentPage(validPage)
    },
    [pagination, deviceCount],
  )

  // Calculate pagination values when pagination is enabled
  const paginationControls =
    pagination && deviceCount
      ? {
          currentPage,
          pageSize: pagination.pageSize,
          totalPages: Math.ceil(deviceCount / pagination.pageSize),
          hasNextPage:
            deviceCount > 0 &&
            currentPage < Math.ceil(deviceCount / pagination.pageSize) - 1,
          hasPreviousPage: currentPage > 0,
          goToPage,
        }
      : undefined

  return {
    deviceCount,
    paginatedDevices: paginatedDevices || [],
    findDevice,
    findDevicePaginated,
    fetchSearchCount,
    fetchDevicesByPeerKeys,
    fetchMetadataSample,
    isLoadingPaginated,
    isRefetchingPaginated,
    errorMessage,
    fetchLogs,
    fetchBundle,
    fetchFlamegraph,
    fetchMetrics,
    requestLogs: handleRequestLogs,
    requestBundle: handleRequestBundle,
    requestFlamegraph: handleRequestFlamegraph,
    requestMetrics: handleRequestMetrics,
    bulkRequestLogs: handleBulkRequestLogs,
    bulkRequestBundle: handleBulkRequestBundle,
    bulkRequestFlamegraph: handleBulkRequestFlamegraph,
    bulkRequestMetrics: handleBulkRequestMetrics,
    cancelDataRequest: handleCancelDataRequest,
    bulkCancelDataRequests: handleBulkCancelDataRequests,
    refetchPaginated: handleRefetchPaginated,
    pagination: paginationControls,
    search: {
      query: searchQuery,
      count: searchTotalCount,
      results: searchResults,
      isLoading: isSearching,
      enabled: isInSearchMode,
      page: searchPage,
      pageSize: searchPageSize,
      setSearchPage,
      setSearchQuery,
    },
    lastUpdatedFilter,
    setLastUpdatedFilter,
  }
}
